import React, { useEffect } from 'react';
import { Card, Tabs } from 'antd';
import { useSearchParams } from 'react-router';
import {
  MedicineBoxOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  BarChartOutlined,
  HistoryOutlined,
  BuildOutlined,
} from '@ant-design/icons';
import { MedicinesPage } from './medicines/MedicinesPage';
import { SuppliersPage } from './suppliers/SuppliersPage';
import { PurchaseOrdersPage } from './purchase-orders/PurchaseOrdersPage';
import { SalesPage } from './sales/SalesPage';
import { InventoryPage } from './inventory/InventoryPage';
import { TransactionsPage } from './transactions/page';
import { ManufacturersPage } from './manufacturers/ManufacturersPage';

const DEFAULT_TAB = 'medicines';

const tabContentStyle = {
  height: '100%',
  overflow: 'hidden',
};

// Tab name mapping for better display
const getTabDisplayName = (tabKey: string): string => {
  switch (tabKey) {
    case 'medicines':
      return 'Medicines';
    case 'suppliers':
      return 'Suppliers';
    case 'mr':
      return 'MR/Contact Person';
    case 'purchases':
      return 'Purchase Orders';
    case 'sales':
      return 'Sales';
    case 'stocks':
      return 'Inventory';
    case 'transactions':
      return 'Transactions';
    default:
      return 'Medical';
  }
};

export const MedicalPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || DEFAULT_TAB;

  // Update document title when tab changes
  useEffect(() => {
    const tabName = getTabDisplayName(currentTab);
    document.title = `${tabName} - Medical Management`;
  }, [currentTab]);

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const items = [
    {
      key: 'medicines',
      label: (
        <span>
          <MedicineBoxOutlined style={{ marginRight: '8px' }} />
          Medicines
        </span>
      ),
      children: (
        <div style={tabContentStyle}>
          <MedicinesPage />
        </div>
      ),
    },
    {
      key: 'suppliers',
      label: (
        <span>
          <ShopOutlined style={{ marginRight: '8px' }} />
          Suppliers
        </span>
      ),
      children: (
        <div style={tabContentStyle}>
          <SuppliersPage />
        </div>
      ),
    },
    {
      key: 'mr',
      label: (
        <span>
          <BuildOutlined style={{ marginRight: '8px' }} />
          MR/Contact Person
        </span>
      ),
      children: (
        <div style={tabContentStyle}>
          <ManufacturersPage />
        </div>
      ),
    },
    {
      key: 'purchases',
      label: (
        <span>
          <ShoppingCartOutlined style={{ marginRight: '8px' }} />
          Purchases
        </span>
      ),
      children: (
        <div style={tabContentStyle}>
          <PurchaseOrdersPage />
        </div>
      ),
    },
    {
      key: 'sales',
      label: (
        <span>
          <DollarOutlined style={{ marginRight: '8px' }} />
          Sales
        </span>
      ),
      children: (
        <div style={tabContentStyle}>
          <SalesPage />
        </div>
      ),
    },
    {
      key: 'stocks',
      label: (
        <span>
          <BarChartOutlined style={{ marginRight: '8px' }} />
          Stocks
        </span>
      ),
      children: (
        <div style={tabContentStyle}>
          <InventoryPage />
        </div>
      ),
    },
    {
      key: 'transactions',
      label: (
        <span>
          <HistoryOutlined style={{ marginRight: '8px' }} />
          Transactions
        </span>
      ),
      children: (
        <div style={tabContentStyle}>
          <TransactionsPage />
        </div>
      ),
    },
  ];

  return (
    <Card
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        border: 'none',
      }}
      styles={{
        body: {
          flex: 1,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        },
      }}
    >
      <Tabs
        activeKey={currentTab}
        onChange={handleTabChange}
        type="line"
        size="large"
        tabPosition="top"
        items={items}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '0 16px',
        }}
        tabBarStyle={{
          margin: 0,
          // borderBottom: '1px solid #f0f0f0',
          flexShrink: 0,
        }}
      />
    </Card>
  );
};
